"use client"

import { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
  Bot,
  Search,
  Eye,
  Loader,
  Users,
  FileCheck,
  Award,
  TrendingUp,
  ChevronDown,
  AlertTriangle,
  ArrowLeft,
} from "lucide-react"
import { getAllSubmisions } from "./api"

export default function AssignmentDetail() {
  const { assignment_id } = useParams()
  const [submissions, setSubmissions] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [studentsData, setStudentsData] = useState([])
  const [selectedStatus, setSelectedStatus] = useState("All")
  const [selectedEvaluation, setSelectedEvaluation] = useState("All")
  const [searchTerm, setSearchTerm] = useState("")
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" })
  const navigate = useNavigate()
  const [assignmentTitle, setAssignmentTitle] = useState("")

  useEffect(() => {
    const fetchAssignment = async (assignmentId) => {
      try {
        setIsLoading(true)
        setError(null)
        const response = await getAllSubmisions(assignmentId)

        const validAssignments = response.data.filter((submission) =>
          ["submitted", "evaluated"].includes(submission.status.toLowerCase()),
        )
        setAssignmentTitle(validAssignments[0].assignmentTitle || "Untitled Assignment")
        setSubmissions(validAssignments)
      } catch (err) {
        console.error("Error fetching assignments:", err)
        setError("No submisions founds for this assignment")
      } finally {
        setIsLoading(false)
      }
    }

    fetchAssignment(assignment_id)
  }, [assignment_id])

  useEffect(() => {
    if (submissions?.length) {
      const students = submissions.map((submission) => ({
        id: submission._id,
        name: submission.studentName,
        status: submission.status,
        marks: submission.obtainedMarks,
        isEvaluated: submission.status.toLowerCase() === "evaluated",
      }))
      setStudentsData(students)
    } else {
      setStudentsData([])
    }
  }, [submissions])

  const totalStudents = studentsData.length
  const submittedAssignments = studentsData.filter((student) => student.status.toLowerCase() === "submitted").length
  const evaluatedAssignments = studentsData.filter((student) => student.status.toLowerCase() === "evaluated").length
  const averageMarks =
    studentsData.length > 0 ? studentsData.reduce((sum, student) => sum + student.marks, 0) / studentsData.length : 0

  const filteredStudents = studentsData
    .filter((student) => student.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .filter((student) => selectedStatus === "All" || student.status.toLowerCase() === selectedStatus.toLowerCase())
    .filter(
      (student) =>
        selectedEvaluation === "All" ||
        (selectedEvaluation === "Evaluated" && student.isEvaluated) ||
        (selectedEvaluation === "Not Evaluated" && !student.isEvaluated),
    )

  const sortedStudents = [...filteredStudents].sort((a, b) => {
    if (sortConfig.key !== null) {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === "ascending" ? -1 : 1
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === "ascending" ? 1 : -1
      }
    }
    return 0
  })

  const handleSort = (key) => {
    let direction = "ascending"
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending"
    }
    setSortConfig({ key, direction })
  }

  const handleEvaluateAI = (studentId) => {
    navigate(`/assignment/detail/${studentId}/evaluate`)
  }

  const handleViewResponse = (studentId) => {
    navigate(`/assignment/detail/${studentId}/viewResponse`)
  }

  const handleBackToAssignments = () => {
    navigate("/assignments")
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-xl shadow-lg">
          <Loader className="w-16 h-16 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-xl font-semibold text-gray-800">Loading submissions...</p>
          <p className="text-gray-600 mt-2">Please wait while we fetch the data</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="text-center bg-white p-8 rounded-xl shadow-lg max-w-md w-full">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />

          <p className="text-gray-600">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-6 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  if (!isLoading && studentsData.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="text-center bg-white p-8 rounded-xl shadow-lg max-w-md w-full">
          <FileCheck className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">No Submissions Yet</h2>
          <p className="text-gray-600">
            There are no submitted or evaluated assignments for this assignment at the moment.
          </p>
          <button
            onClick={() => navigate("/assignments")}
            className="mt-6 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Back to Assignments
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 -mt-6">
      <div className="max-w-7xl mx-auto">
        {/* Header with Breadcrumb */}
<div className="bg-white rounded-xl md:rounded-2xl shadow-md overflow-hidden mt-6 md:mt-8 mx-2 md:mx-4 mb-8">
  <div className="bg-gradient-to-r from-indigo-500 to-blue-600 px-3 md:px-4 py-[0.5px]">
    <div className="flex items-center justify-between">
      <div className="flex items-center">
                <button
                  onClick={handleBackToAssignments}
                  className="flex items-center text-white hover:text-gray-200 transition-colors duration-200 mt-1"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  <span className="font-medium text-sm">Assignments</span>
                </button>

      </div>
    </div>
    {/* Centered Title Below Button */}
    <div className="mt-2 text-center">
      <h1 className="text-xl font-bold text-white">{assignmentTitle}</h1>
    </div>
  </div>



          {/* Statistics Cards */}
          <div className="p-4 md:p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl shadow-sm">
              <Users className="w-10 h-10 text-blue-600 mb-4" />
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Total Submissions</h2>
              <p className="text-3xl font-bold text-blue-600">{totalStudents}</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl shadow-sm">
              <FileCheck className="w-10 h-10 text-green-600 mb-4" />
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Submitted Assignments</h2>
              <p className="text-3xl font-bold text-green-600">{submittedAssignments}</p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl shadow-sm">
              <Award className="w-10 h-10 text-purple-600 mb-4" />
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Evaluated Assignments</h2>
              <p className="text-3xl font-bold text-purple-600">{evaluatedAssignments}</p>
            </div>
            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-6 rounded-xl shadow-sm">
              <TrendingUp className="w-10 h-10 text-yellow-600 mb-4" />
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Average Score</h2>
              <p className="text-3xl font-bold text-yellow-600">{averageMarks.toFixed(2)}</p>
            </div>
          </div>
        </div>

        {/* Submissions Table */}
        <div className="bg-white rounded-xl md:rounded-2xl shadow-md overflow-hidden mx-2 md:mx-4">
          <div className="p-4 md:p-6">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
              <div className="w-full sm:w-64 relative">
                <input
                  type="text"
                  placeholder="Search students..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              </div>
              <div className="w-full sm:w-auto flex flex-col sm:flex-row gap-4">
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full sm:w-auto px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="All">All Statuses</option>
                  <option value="Submitted">Submitted</option>
                  <option value="Evaluated">Evaluated</option>
                </select>
                <select
                  value={selectedEvaluation}
                  onChange={(e) => setSelectedEvaluation(e.target.value)}
                  className="w-full sm:w-auto px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="All">All Evaluations</option>
                  <option value="Evaluated">Evaluated</option>
                  <option value="Not Evaluated">Not Evaluated</option>
                </select>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button className="flex items-center" onClick={() => handleSort("name")}>
                        Name
                        {sortConfig.key === "name" && (
                          <ChevronDown
                            size={16}
                            className={`ml-1 ${sortConfig.direction === "descending" ? "transform rotate-180" : ""}`}
                          />
                        )}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button className="flex items-center" onClick={() => handleSort("status")}>
                        Status
                        {sortConfig.key === "status" && (
                          <ChevronDown
                            size={16}
                            className={`ml-1 ${sortConfig.direction === "descending" ? "transform rotate-180" : ""}`}
                          />
                        )}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button className="flex items-center" onClick={() => handleSort("marks")}>
                        Marks
                        {sortConfig.key === "marks" && (
                          <ChevronDown
                            size={16}
                            className={`ml-1 ${sortConfig.direction === "descending" ? "transform rotate-180" : ""}`}
                          />
                        )}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedStudents.map((student, index) => (
                    <tr key={student.id} className="hover:bg-gray-50 transition-colors duration-150 ease-in-out">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{student.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            student.status.toLowerCase() === "evaluated"
                              ? "bg-green-100 text-green-800"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {student.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{student.marks}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleEvaluateAI(student.id)}
                          className={`mr-2 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md ${
                            student.isEvaluated
                              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                              : "bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                          } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                          disabled={student.isEvaluated}
                        >
                          <Bot size={16} className="mr-1" />
                          Evaluate AI
                        </button>
                        <button
                          onClick={() => handleViewResponse(student.id)}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          <Eye size={16} className="mr-1" />
                          View Response
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
